export type ToastLevel = "info" | "warn" | "error";

export class StickyToast {
  // 플레이어가 여러 개(web-player-audio01/02)이고 모두 같은 audioContext의
  // statechange를 수신하므로, 인스턴스를 따로 두면 토스트가 겹쳐서 쌓인다.
  // 화면에는 항상 하나만 보이도록 공용 인스턴스를 사용한다.
  private static instance: StickyToast | null = null;

  static get shared(): StickyToast {
    if (!StickyToast.instance) StickyToast.instance = new StickyToast();
    return StickyToast.instance;
  }

  private el: HTMLDivElement | null = null;
  private msgEl: HTMLDivElement | null = null;
  private detailEl: HTMLPreElement | null = null;
  private detailBtn: HTMLButtonElement | null = null;

  private hideTimer: number | null = null;
  private readonly AUTO_HIDE_MS = 6000;

  private clearHideTimer() {
    if (this.hideTimer != null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private startHideTimer(duration: number) {
    this.clearHideTimer();
    if (duration <= 0) return;
    this.hideTimer = window.setTimeout(() => {
      this.hideTimer = null;
      this.hide();
    }, duration);
  }

  private static readonly ELEMENT_ID = "__onair_sticky_toast__";

  private ensure() {
    if (this.el) return;

    // 이전에 만들어진 엘리먼트가 남아 있으면 새로 만들지 않고 정리한다
    const stale = document.getElementById(StickyToast.ELEMENT_ID);
    if (stale) stale.remove();

    const el = document.createElement("div");
    el.id = StickyToast.ELEMENT_ID;
    el.style.position = "fixed";
    el.style.left = "12px";
    el.style.right = "12px";
    el.style.bottom = "12px";
    el.style.zIndex = "2147483647";
    el.style.padding = "12px";
    el.style.borderRadius = "10px";
    el.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
    el.style.fontSize = "14px";
    el.style.lineHeight = "1.35";
    el.style.display = "none";
    el.style.whiteSpace = "normal";
    el.style.wordBreak = "break-word";
    el.style.userSelect = "text";

    const header = document.createElement("div");
    header.style.display = "flex";
    header.style.alignItems = "flex-start";
    header.style.gap = "10px";

    const textWrap = document.createElement("div");
    textWrap.style.flex = "1";

    const msgEl = document.createElement("div");
    msgEl.style.fontWeight = "700";
    msgEl.style.marginBottom = "6px";

    const detailEl = document.createElement("pre");
    detailEl.style.margin = "0";
    detailEl.style.padding = "8px";
    detailEl.style.borderRadius = "8px";
    detailEl.style.background = "rgba(255,255,255,0.12)";
    detailEl.style.overflow = "auto";
    detailEl.style.maxHeight = "180px";
    detailEl.style.display = "none";
    detailEl.style.fontSize = "12px";

    textWrap.appendChild(msgEl);
    textWrap.appendChild(detailEl);

    const btns = document.createElement("div");
    btns.style.display = "flex";
    btns.style.flexDirection = "column";
    btns.style.gap = "6px";

    const detailBtn = document.createElement("button");
    detailBtn.textContent = "자세히";
    detailBtn.style.height = "34px";
    detailBtn.style.borderRadius = "8px";
    detailBtn.style.border = "none";
    detailBtn.style.cursor = "pointer";
    detailBtn.style.padding = "0 10px";
    detailBtn.onclick = () => {
      if (!this.detailEl) return;
      const show = this.detailEl.style.display === "none";
      this.detailEl.style.display = show ? "block" : "none";
      if (this.detailBtn) this.detailBtn.textContent = show ? "접기" : "자세히";
      // 상세를 펼치면 읽는 중이므로 자동 닫힘을 취소한다
      if (show) this.clearHideTimer();
      else this.startHideTimer(this.AUTO_HIDE_MS);
    };

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.style.width = "34px";
    closeBtn.style.height = "34px";
    closeBtn.style.borderRadius = "8px";
    closeBtn.style.border = "none";
    closeBtn.style.cursor = "pointer";
    closeBtn.onclick = () => this.hide();

    btns.appendChild(detailBtn);
    btns.appendChild(closeBtn);

    header.appendChild(textWrap);
    header.appendChild(btns);
    el.appendChild(header);

    document.body.appendChild(el);

    this.el = el;
    this.msgEl = msgEl;
    this.detailEl = detailEl;
    this.detailBtn = detailBtn;
  }

  /**
   * @param duration 자동 닫힘까지의 시간(ms). 0 이하면 자동으로 닫지 않는다.
   */
  show(message: string, level: ToastLevel = "info", detail?: any, duration: number = this.AUTO_HIDE_MS) {
    console.log("# toast", message, level);
    this.ensure();
    if (!this.el || !this.msgEl || !this.detailEl) return;

    if (level === "info") {
      this.el.style.background = "rgba(20, 24, 30, 0.92)";
      this.el.style.color = "white";
    } else if (level === "warn") {
      this.el.style.background = "rgba(70, 50, 0, 0.92)";
      this.el.style.color = "white";
    } else {
      this.el.style.background = "rgba(70, 0, 0, 0.92)";
      this.el.style.color = "white";
    }

    this.msgEl.textContent = message;

    // 상세는 기본으로 접어두고 "자세히"를 눌렀을 때만 보여준다
    if (detail !== undefined) {
      try {
        this.detailEl.textContent =
          typeof detail === "string" ? detail : JSON.stringify(detail, null, 2);
      } catch {
        this.detailEl.textContent = String(detail);
      }
      if (this.detailBtn) this.detailBtn.style.display = "";
    } else {
      this.detailEl.textContent = "";
      // 볼 내용이 없으면 "자세히" 버튼도 감춘다
      if (this.detailBtn) this.detailBtn.style.display = "none";
    }
    this.detailEl.style.display = "none";
    if (this.detailBtn) this.detailBtn.textContent = "자세히";

    this.el.style.display = "block";
    this.startHideTimer(duration);
  }

  hide() {
    this.clearHideTimer();
    if (!this.el) return;
    this.el.style.display = "none";
  }
}
